import { alpha, colors } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import type {} from '@mui/x-date-pickers/themeAugmentation'

import { brand, isHappa } from '@/config/brand'

export const customColors = {
  blue: {
    50: brand.colors.primary[50],
    100: brand.colors.primary[100],
    200: brand.colors.primary[200],
    300: brand.colors.primary[300],
    400: brand.colors.primary[400],
    500: brand.colors.primary[500],
    600: brand.colors.primary[600],
    700: brand.colors.primary[700],
    800: brand.colors.primary[800],
    900: brand.colors.primary[900],
    a100: '#6E92FF',
    a200: '#3A6BFF',
    a400: '#0F49FF',
    a700: '#0036E0'
  },
  yellow: {
    50: brand.colors.yellow[50],
    100: brand.colors.yellow[100],
    200: brand.colors.yellow[200],
    300: brand.colors.yellow[300],
    400: brand.colors.yellow[400],
    500: brand.colors.yellow[500],
    600: brand.colors.yellow[600],
    700: brand.colors.yellow[700],
    800: brand.colors.yellow[800],
    900: brand.colors.yellow[900],
    a100: '#FFE082',
    a200: '#FFD54F',
    a400: '#FFC107',
    a700: '#FFB300'
  }
}

import { breakpointVal } from '@/utils'

const breakpoints = {
  xs: 0,
  sm: 768,
  md: 1024,
  lg: 1200,
  xl: 1536
}

const fontSize = (from: number, to: number) =>
  breakpointVal('fontSize', from, to, 'rem', breakpoints)

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    textLarge: true
    textRegular: true
    textSmall: true
    menu: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    opacity: true
  }
  interface ButtonPropsColorOverrides {
    neutral: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    accent: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    highlight: true
    color: true
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    textLarge: React.CSSProperties
    textRegular: React.CSSProperties
    textSmall: React.CSSProperties
    menu: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    textLarge?: React.CSSProperties
    textRegular?: React.CSSProperties
    textSmall?: React.CSSProperties
    menu?: React.CSSProperties
  }

  interface TypeText {
    primaryContrastText: string
    secondaryContrastText: string
  }
  interface PaletteColor {
    secondary?: string
  }
  interface SimplePaletteColorOptions {
    secondary?: string
  }
  interface TypeAction {
    activeBackground: string
  }
  interface Palette {
    surface: {
      background: string
    }
    blue: PaletteColor
  }
  interface PaletteOptions {
    surface?: {
      background?: string
    }
    blue?: SimplePaletteColorOptions
  }

  interface Shape {
    none: number
    borderRadius1: number
    borderRadius2: number
    borderRadius3: number
    borderRadius4: number
    borderRadius5: number
    borderRadius6: number
    borderRadius7: number
    borderRadius8: number
    borderRadius9: number
    borderRadius10: number
    borderRadius11: number
    borderRadius360: number
  }

  interface ShapeOptions {
    none?: number
    borderRadius1?: number
    borderRadius2?: number
    borderRadius3?: number
    borderRadius4?: number
    borderRadius5?: number
    borderRadius6?: number
    borderRadius7?: number
    borderRadius8?: number
    borderRadius9?: number
    borderRadius10?: number
    borderRadius11?: number
    borderRadius360?: number
  }

  // Estensione per customColors
  interface Theme {
    customColors: typeof customColors
  }
  interface ThemeOptions {
    customColors?: typeof customColors
  }
  // Estensione per customEasings
  interface Theme {
    customColors: typeof customColors
    customEasings: typeof customEasings
    customSizes: typeof customSizes
  }
  interface ThemeOptions {
    customColors?: typeof customColors
    customEasings?: typeof customEasings
    customSizes?: typeof customSizes
  }
}
// Easing personalizzato
export const customEasings = {
  outCubic: 'cubic-bezier(0.215, 0.61, 0.355, 1)'
}

// Dimensioni custom
export const customSizes = {
  headerLogoHeight: brand.sizes.headerLogoHeight,
  headerLogoHeightMinimized: brand.sizes.headerLogoHeightMinimized,
  headerHeight: brand.sizes.headerHeight
}

const theme = createTheme({
  breakpoints: {
    values: breakpoints
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.08)',
    '0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.08)',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    ''
  ],
  palette: {
    mode: 'light',
    divider: alpha('#000000', 0.12),
    text: {
      primary: colors.grey[700],
      secondary: colors.grey[600],
      primaryContrastText: colors.grey[50],
      secondaryContrastText: colors.grey[900]
    },
    primary: {
      main: customColors.blue[500],
      dark: customColors.blue[700],
      light: customColors.blue[300]
    },
    secondary: {
      main: brand.colors.secondary.main,
      secondary: brand.colors.secondary.dark,
      light: brand.colors.secondary.light
    },
    action: {
      active: alpha('#000000', 0.64),
      hover: alpha('#000000', 0.04),
      selected: alpha('#000000', 0.08),
      focus: alpha('#000000', 0.12),
      disabled: alpha('#000000', 0.38),
      activeBackground: alpha('#000000', 0.02),
      disabledBackground: alpha('#000000', 0.12)
    },
    error: {
      main: colors.red[700],
      dark: colors.red[800],
      light: colors.red[400],
      contrastText: '#ffffff',
      50: colors.red[50],
      100: colors.red[100],
      200: colors.red[200],
      300: colors.red[300],
      400: colors.red[400],
      500: colors.red[500],
      600: colors.red[600],
      700: colors.red[700],
      800: colors.red[800],
      900: colors.red[900]
    },
    warning: {
      main: colors.orange[800],
      dark: colors.orange[900],
      light: colors.orange[500],
      contrastText: '#ffffff',
      50: colors.orange[50],
      100: colors.orange[100],
      200: colors.orange[200],
      300: colors.orange[300],
      400: colors.orange[400],
      500: colors.orange[500],
      600: colors.orange[600],
      700: colors.orange[700],
      800: colors.orange[800],
      900: colors.orange[900]
    },
    info: {
      main: colors.lightBlue[700],
      dark: colors.lightBlue[900],
      light: colors.lightBlue[500],
      contrastText: '#ffffff'
    },
    success: {
      main: colors.green[800],
      dark: colors.green[900],
      light: colors.green[500],
      contrastText: '#ffffff',
      50: colors.green[50],
      100: colors.green[100],
      200: colors.green[200],
      300: colors.green[300],
      400: colors.green[400],
      500: colors.green[500],
      600: colors.green[600],
      700: colors.green[700],
      800: colors.green[800],
      900: colors.green[900]
    },
    surface: {
      background: '#fafafa'
    }
  },
  shape: {
    none: 0,
    borderRadius1: 1, // 4
    borderRadius2: 2, // 8
    borderRadius3: 3, // 12
    borderRadius4: 4, // 16
    borderRadius5: 5, // 20
    borderRadius6: 6, // 24
    borderRadius7: 8, // 32
    borderRadius8: 10, // 40
    borderRadius9: 12, // 48
    borderRadius10: 14, // 56
    borderRadius11: 16, // 64
    borderRadius360: 90 // 360
  },
  spacing: (factor: number) => {
    const values = [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 224]
    if (Number.isInteger(factor) && factor >= 0 && factor < values.length) {
      return values[factor]
    }
    return factor * 8 // fallback for non-integer or out of bounds
  },
  typography: {
    htmlFontSize: 10,
    fontFamily: brand.fonts.body,
    h1: {
      fontFamily: brand.fonts.heading,
      ...fontSize(6.1, 7.6),
      fontWeight: 500,
      letterSpacing: '-0.38rem',
      lineHeight: '110%'
    },
    h2: {
      fontFamily: brand.fonts.heading,
      ...fontSize(4.88, 6.1),
      fontWeight: 500,
      letterSpacing: '-0.24rem',
      lineHeight: '100%'
    },
    h3: {
      fontFamily: brand.fonts.heading,
      ...fontSize(3.9, 4.88),
      fontWeight: 500,
      letterSpacing: '-0.14rem',
      lineHeight: '120%'
    },
    h4: {
      fontFamily: brand.fonts.heading,
      ...fontSize(3.1, 3.9),
      fontWeight: 500,
      letterSpacing: '-0.05rem',
      lineHeight: '120%'
    },
    h5: {
      fontFamily: brand.fonts.heading,
      ...fontSize(2.5, 3.1),
      fontWeight: 500,
      letterSpacing: '0rem',
      lineHeight: '120%'
    },
    h6: {
      fontFamily: brand.fonts.heading,
      ...fontSize(2, 2.5),
      fontWeight: 500,
      letterSpacing: '0rem',
      lineHeight: '120%'
    },
    textLarge: {
      fontSize: '2rem',
      fontWeight: 400,
      letterSpacing: '0rem',
      lineHeight: '120%'
    },
    textRegular: {
      fontSize: '1.6rem',
      fontWeight: 400,
      letterSpacing: '0rem',
      lineHeight: '130%'
    },
    textSmall: {
      fontSize: '1.28rem',
      fontWeight: 400,
      letterSpacing: '0rem',
      lineHeight: '140%'
    },
    caption: {
      fontSize: '1.4rem',
      fontWeight: 500,
      letterSpacing: '0.02rem',
      lineHeight: '150%'
    },
    overline: {
      fontSize: '0.81rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.08rem',
      lineHeight: '160%'
    },
    menu: {
      fontFamily: brand.fonts.button,
      fontSize: '1.6rem',
      fontWeight: 500,
      letterSpacing: '-0.03rem',
      lineHeight: '170%'
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: '10px', // '62.5%',
          ['@media (hover: hover) and (pointer: fine)']: {
            scrollbarWidth: 'none' /* Firefox */,
            msOverflowStyle: 'none' /* IE/Edge */,
            '&::-webkit-scrollbar': {
              display: 'none' /* Chrome, Safari, Opera */
            }
          }
        },
        body: {
          background: colors.grey[50],
          color: colors.grey[900],
          fontSize: '1.6rem',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        },
        '#__next': {
          position: 'static'
        },
        img: {
          userSelect: 'none'
        },
        'html.lenis, html.lenis body': {
          height: 'auto'
        },
        '.lenis [data-lenis-prevent], .lenis [data-lenis-prevent-wheel], .lenis [data-lenis-prevent-touch]':
          {
            overscrollBehavior: 'contain'
          },
        '.lenis.lenis-smooth iframe': {
          pointerEvents: 'none'
        },
        '.lenis.lenis-autoToggle': {
          transitionProperty: 'overflow',
          transitionDuration: '1ms',
          transitionBehavior: 'allow-discrete'
        },
        '& p, & .MuiTypography-textRegular': {
          a: {
            color: customColors.blue[500],
            textDecoration: 'none',
            '&:hover': {
              color: customColors.blue[700],
              textDecoration: 'underline'
            }
          }
        }
      }
    },
    MuiContainer: {
      defaultProps: { disableGutters: false, maxWidth: 'xl' },
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: '1.6rem',
          paddingRight: '1.6rem',
          [theme.breakpoints.up('sm')]: {
            paddingLeft: '2.4rem',
            paddingRight: '2.4rem'
          },
          [theme.breakpoints.up('md')]: {
            paddingLeft: '3.2rem',
            paddingRight: '3.2rem'
          }
        })
      }
    },
    MuiGrid: {
      defaultProps: {
        spacing: { xs: '1.6rem', sm: '2.4rem', md: '3.2rem' },
        columns: { xs: 4, sm: 8, md: 12 }
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          textLarge: 'p',
          textRegular: 'div',
          textSmall: 'p',
          caption: 'span',
          overline: 'span',
          menu: 'span'
        }
      }
    },
    MuiSvgIcon: {
      defaultProps: {
        viewBox: '0 0 24 24',
        fontSize: 'small'
      },
      styleOverrides: {
        fontSizeSmall: {
          fontSize: '1.6rem'
        },
        fontSizeMedium: {
          fontSize: '2.4rem'
        },
        fontSizeLarge: {
          fontSize: '3.2rem'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '999px',
          typography: 'menu',
          textTransform: 'none',
          height: '4.2rem',
          fontFamily: brand.fonts.button,
          variants: [
            {
              props: { variant: 'contained', color: 'secondary' },
              style: {
                backgroundColor: isHappa ? customColors.yellow[500] : '#FAFAFA',
                color: customColors.blue[500],
                '&:hover': {
                  backgroundColor: isHappa ? customColors.yellow[600] : colors.grey[300]
                }
              }
            },
            {
              props: { variant: 'contained', color: 'info' },
              style: {
                backgroundColor: 'rgba(172, 58, 49, 0.04)',
                color: customColors.blue[500],
                '&:hover': {
                  backgroundColor: colors.grey[300]
                }
              }
            },
            {
              props: { variant: 'contained', color: 'warning' },
              style: {
                backgroundColor: '#FFC107',
                color: '#212121',
                '&:hover': {
                  backgroundColor: `${alpha('#FFC107', 0.5)}`
                }
              }
            },
            {
              props: { variant: 'opacity' },
              style: {
                color: customColors.blue[500],
                backgroundColor: alpha(customColors.blue[500], 0.06),
                '&:hover': {
                  backgroundColor: alpha(customColors.blue[500], 0.1)
                }
              }
            },
            {
              props: { variant: 'text', color: 'warning' },
              style: {
                padding: '8px !important',
                height: 'auto !important',
                // borderRadius: '8px !important',
                color: '#616161',
                '&:hover': {
                  backgroundColor: colors.grey[100]
                }
              }
            },
            {
              props: { variant: 'text', color: 'primary' },
              style: {
                padding: '8px !important',
                height: 'auto !important',
                // borderRadius: '8px !important',
                color: customColors.blue[500],
                '&:hover': {
                  backgroundColor: colors.grey[100]
                }
              }
            },
            ...(isHappa
              ? [
                  {
                    props: { variant: 'contained' as const, color: 'neutral' as const },
                    style: {
                      backgroundColor: '#FAFAFA',
                      color: customColors.blue[500],
                      '&:hover': {
                        backgroundColor: colors.grey[300]
                      }
                    }
                  }
                ]
              : [])
          ]
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: `${alpha('#000000', 0.04)}`,
            color: `${alpha('#000000', 0.32)}`
          },
          variants: [
            {
              props: { size: 'small' },
              style: {
                width: '2.4rem',
                height: '2.4rem'
              }
            },
            {
              props: { size: 'medium' },
              style: {
                width: '4.8rem',
                height: '4.8rem'
              }
            },
            {
              props: { size: 'large' },
              style: {
                width: '5.2rem',
                height: '5.2rem'
              }
            },
            {
              props: { color: 'primary' },
              style: {
                backgroundColor: customColors.blue[500],
                color: '#FAFAFA',
                '&:hover': {
                  backgroundColor: `${alpha(customColors.blue[500], 0.8)}`
                }
              }
            },
            {
              props: { color: 'secondary' },
              style: {
                backgroundColor: `${alpha(customColors.blue[500], 0.04)}`,
                color: customColors.blue[500],
                '&:hover': {
                  backgroundColor: `${alpha(customColors.blue[500], 0.08)}`
                }
              }
            },
            ...(isHappa
              ? [
                  {
                    props: { color: 'accent' as const },
                    style: {
                      backgroundColor: customColors.yellow[500],
                      color: '#FAFAFA',
                      '&:hover': {
                        backgroundColor: colors.yellow[600]
                      }
                    }
                  }
                ]
              : [])
          ]
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '',
          boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.08)',
          borderRadius: 999,
          padding: '0.5rem',
          minHeight: 0,
          '& .MuiTabs-indicator': {
            height: '100%',
            backgroundColor: customColors.blue[500],
            borderRadius: 999
          }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '1.6rem',
          lineHeight: '170%',
          backgroundColor: '',
          padding: '0.5rem 1.6rem',
          textTransform: 'none',
          color: customColors.blue[500],
          borderRadius: 999,
          minHeight: 0,
          zIndex: 1,
          transition: 'color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          '&.Mui-selected': {
            color: 'white'
          }
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          '&:before': {
            border: 0
          },
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '1px',
            backgroundColor: alpha('#000000', 0.12)
          }
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '1.6rem 1.6rem 1.6rem 0',
          gap: '1.6rem',
          '@media (min-width:768px)': {
            padding: '3.2rem 1.6rem 3.2rem 0',
            gap: '3.2rem'
          },
          '@media (min-width:1024px)': {
            padding: '3.2rem 3.2rem 3.2rem 0'
          }
        }
      }
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '0 0 1.6rem 0',
          '@media (min-width:768px)': {
            padding: '0 0 3.2rem 0'
          },
          '@media (min-width:1024px)': {}
        }
      }
    },
    MuiModal: {
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'start',
          padding: '1.6rem',
          overflow: 'auto'
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: customColors.blue[500],
          '&.Mui-checked': {
            color: customColors.blue[500]
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.6rem'
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '& [type="checkbox"] + div': {
            marginTop: '-2px'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '1.28rem',
          borderRadius: '10rem',
          padding: '0.4rem 0.2rem',
          variants: [
            {
              props: { variant: 'highlight' },
              style: {
                color: colors.grey[50],
                backgroundColor: customColors.blue[500]
              }
            },
            {
              props: { variant: 'highlight', color: 'secondary' },
              style: {
                color: colors.grey[900],
                backgroundColor: colors.yellow[500]
              }
            },
            {
              props: { variant: 'color' },
              style: {
                color: customColors.blue[500],
                backgroundColor: alpha(customColors.blue[500], 0.04)
              }
            },
            {
              props: { variant: 'filled' },
              style: {
                color: '#FAFAFA',
                backgroundColor: alpha('#FAFAFA', 0.16)
              }
            }
          ]
        }
      }
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          variants: [
            {
              props: { color: 'primary' },
              style: {
                minWidth: 'auto',
                width: '1.4rem',
                height: '1.4rem',
                fontSize: '0.8rem',
                padding: '0 0.4rem',
                backgroundColor: customColors.blue[500],
                color: '#fff',
                top: 0,
                right: 0
              }
            }
          ]
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center'
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '80px',
          '@media (min-width:768px)': {
            minHeight: '80px'
          }
        }
      }
    }
  },
  customColors,
  customEasings,
  customSizes
})

export default theme
